<script setup lang="ts">
import { matchesSearchAccentInsensitive } from '@/utils/string/search-normalize'
import { ref, onMounted, watch, computed } from 'vue'
import { getSensorColor, translateSensorType } from '@/interfaces/enums.ts'
import { ISensor } from '@/interfaces/entities/sensor.ts'
import { useField } from 'vee-validate'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    name: string
    options: Array<ISensor>
    alreadySelectedOption?: ISensor
    hasAllOption?: boolean
    /** Quando true, não força min-width grande (evita vazar sobre colunas vizinhas em toolbars) */
    constrainToParent?: boolean
  }>(),
  { constrainToParent: false },
)

const emit = defineEmits<{
  (e: 'option-selected', payload: ISensor | typeof allObjectsOption): void
}>()

const isOptionsExpanded = ref<boolean>(false)
const selectedOption = ref<ISensor | typeof allObjectsOption>(null)
const sensorName = ref<string>('')
const { value: inputValue, errorMessage } = useField<ISensor | typeof allObjectsOption>(props.name)

const allObjectsOption = {
  id: null,
  name: 'Todos',
  sensor_type: null,
}

const filteredOptions = computed(() => {
  return props.options.filter((option) => matchesSearchAccentInsensitive(option.name, sensorName.value))
})

const rootClass = computed(() =>
  props.constrainToParent ? 'relative min-w-0 w-full max-w-full overflow-visible' : 'relative min-w-75',
)

const listClass = computed(() =>
  [
    'absolute left-0 right-0 z-50 mt-1 overflow-y-auto overscroll-contain bg-white shadow-lg',
    props.constrainToParent ? 'max-h-48' : 'max-h-96',
  ].join(' '),
)

const inputRef = ref<HTMLInputElement | null>(null)

function setOption(option: ISensor | undefined) {

  if (props.hasAllOption && option === undefined) {
    // Se a opção "Todos" for selecionada
    inputValue.value = allObjectsOption
    selectedOption.value = allObjectsOption
    sensorName.value = t('globals.dropdowns.all')
    emit('option-selected', allObjectsOption)
    isOptionsExpanded.value = false
    inputRef.value?.blur()
    return
  }

  selectedOption.value = option
  inputValue.value = option
  sensorName.value = option?.name || ''
  isOptionsExpanded.value = false
  emit('option-selected', option)
  inputRef.value?.blur()
}

function syncSensorNameFromValue() {
  if (props.hasAllOption && inputValue.value && typeof inputValue.value === 'object' && inputValue.value.id === null) {
    sensorName.value = t('globals.dropdowns.all')
    return
  }
  if (selectedOption.value && typeof selectedOption.value === 'object' && selectedOption.value.name) {
    sensorName.value = selectedOption.value.name
    return
  }
  sensorName.value = ''
}

function handleFocus() {
  isOptionsExpanded.value = true
  sensorName.value = ''
}

function handleBlur() {
  isOptionsExpanded.value = false
  syncSensorNameFromValue()
}

onMounted(() => {
  if (props.alreadySelectedOption) {
    setOption(props.alreadySelectedOption)
  }
})

watch(inputValue, (newValue) => {
  if (newValue !== selectedOption.value) {
    selectedOption.value = newValue
  }
})

watch(sensorName, (newValue) => {
  if (newValue === '') return
  if (newValue !== selectedOption.value?.name) {
    selectedOption.value = null
    isOptionsExpanded.value = document.activeElement === inputRef.value
  }
})
</script>

<template>
  <div :class="rootClass">
    <input
      ref="inputRef"
      v-model="sensorName"
      type="text"
      class="w-full border border-zinc-300 bg-white py-3 pl-4 pr-10 transition-colors hover:border-zinc-400 focus:border-primary focus:outline-none"
      :placeholder="t('placeholders.sensors.select_sensor')"
      @focus="handleFocus"
      @blur="handleBlur" />
    <div class="pointer-events-auto absolute inset-y-0 right-8 flex items-center pr-3">
      <span v-if="selectedOption && selectedOption.sensor_type" :class="`p-2 py-1 text-sm font-medium text-white ${getSensorColor(selectedOption.sensor_type)}`">
        {{ translateSensorType(selectedOption.sensor_type) }}
      </span>
    </div>
    <div class="pointer-events-auto absolute inset-y-0 right-0 flex items-center pr-3">
      <svg
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        class="h-4 w-4 transform text-zinc-400 transition-transform duration-200 ease-in-out"
        :class="isOptionsExpanded ? 'rotate-180' : 'rotate-0'">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </div>

    <ul
      v-show="isOptionsExpanded"
      :class="listClass"
      @wheel.stop>
      <li
        v-if="props.hasAllOption"
        class="cursor-pointer bg-green-500 px-3 py-2 text-white transition-colors duration-300 hover:bg-black"
        @mousedown.prevent="setOption(undefined)">
        Todos
      </li>
      <li
        v-for="(option, index) in filteredOptions"
        :key="index"
        class="cursor-pointer px-3 py-2 font-medium transition-colors duration-300 hover:bg-lightGray"
        @click.stop="setOption(option)"
        @mousedown.prevent>
        <div class="flex items-center space-x-4">
          <span>{{ option.name }}</span>
          <span :class="`px-2 py-1 text-sm font-medium text-white ${getSensorColor(option.sensor_type)}`">
            {{ translateSensorType(option.sensor_type) }}
          </span>
        </div>
      </li>
    </ul>

    <p v-if="errorMessage" class="mt-1 text-xs text-red-500">
      {{ errorMessage }}
    </p>
  </div>
</template>
